/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import React, { useLayoutEffect } from 'react'
import { Flex } from '../../../components/Layout'

import RegistrationDivision from './Division'
import RegistrationIndicatorCard from './RegistrationIndicatorCard'
import { observer } from 'mobx-react-lite'
import { useMobxStore } from '../../../state'
import LoadingSpinner from '../../../components/LoadingSpinner'

import emptyRegistration from './empty_registration.png'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { ROUTES } from '@sportninja/common/constants/app'
import { useHistory } from 'react-router-dom'
import req from '@sportninja/common/api/request'
import { useI18n } from '@shopify/react-i18n'

const generateHref = (string, reportName, ref) => {
  var blob = new Blob([string], { type: 'text/csv;charset=utf-8;' })

  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, reportName)
  } else {
    var a = document.createElement('a')
    document.body.appendChild(a)
    a.style = 'display: none'
    var csvUrl = URL.createObjectURL(blob)
    a.href = csvUrl
    a.download = reportName
    a.click()
    URL.revokeObjectURL(a.href)
    a.remove()
  }
}

dayjs.extend(relativeTime)

const Registration = ({ scheduleId }) => {
  const history = useHistory()
  const downloadRef = React.useRef(null)
  const [loading, setLoading] = React.useState(false)

  const [i18n] = useI18n()

  const onView = async () => {
    try {
      const response = await req(
        `/reports/${scheduleId}/registration?format=csv`,
        { parseJSON: false }
      )
      const text = await response.text()
      generateHref(text, 'registration', downloadRef)
    } catch (e) {
      console.error(e)
      alert(
        `There was an error generating the report. ${
          e?.message || ''
        } Please try again.`
      )
    }
  }

  const {
    registrationTypes: { fetchTypes },
    registration: {
      loadDivisions,
      state: {
        divisions,
        isLoading,
        error,
        registration,
        analytics,
        enableRegistrationSetup,
      },
    },
  } = useMobxStore()

  useLayoutEffect(() => {
    fetchTypes()
    loadDivisions(scheduleId)
  }, [])

  if (isLoading) {
    return (
      <div
        css={css`
          padding-top: 20%;
        `}
      >
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          padding-top: 10%;
        `}
      >
        <span>{error}</span>
      </div>
    )
  }

  if (!registration || divisions.length === 0) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding-top: 150px;
        `}
      >
        <div
          css={css`
            width: 426px;
            height: 251px;
          `}
        >
          <img
            src={emptyRegistration}
            css={css`
              width: 100%;
              height: 100%;
            `}
          />
        </div>
        <p
          css={css`
            margin-top: 10px;
            font-weight: 600;
            font-size: 24px;
            color: ${colors.WHITE};
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'Rift, Arial, Helvetica, sans-serif'};
          `}
        >
          WOULD YOU LIKE TO SETUP REGISTRATION FOR THIS COMPETITION?
        </p>
        {enableRegistrationSetup ? (
          <button
            onClick={() =>
              history.push(`${ROUTES.SETUP_REGISTRATION}`, { scheduleId })
            }
          >
            <p
              css={css`
                color: ${colors.DEFAULT_FLAIR};
                margin-top: 10px;
                font-size: 16px;
              `}
            >
              Set up Registration
            </p>
          </button>
        ) : (
          <button
            onClick={() =>
              history.push(`${ROUTES.SCHEDULE_ROOT}/${scheduleId}/setup`)
            }
          >
            <p
              css={css`
                color: ${colors.DEFAULT_FLAIR};
                margin-top: 10px;
                font-size: 16px;
              `}
            >
              Go to Setup Tab
            </p>
          </button>
        )}
      </div>
    )
  }

  return (
    <Flex
      css={css`
        margin-top: 40px;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 5;
        `}
      >
        {divisions.map((division) => {
          return <RegistrationDivision key={division.id} division={division} />
        })}
      </div>
      <div
        css={css`
          display: none;

          @media (min-width: 1160px) {
            display: flex;
            flex-direction: column;
            flex: 1.1;
            margin-left: 16px;
          }
        `}
      >
        <RegistrationIndicatorCard
          iconName='user-plus'
          title='Registrations'
          mainText={analytics?.registrations?.total || 0}
          subText={`${(
            (analytics?.registrations?.last_7_days_trend_percentage || 0) * 100
          ).toFixed(2)}% in last 7 days`}
          hasIncreased={analytics?.registrations?.last_7_days_trend === 'up'}
        />
        <RegistrationIndicatorCard
          iconName='dollar-sign'
          title='Revenue'
          mainText={
            i18n.formatCurrency(analytics?.revenue?.total, {
              currency: registration?.currency?.name,
              form: 'explicit',
            }) || 0
          }
          subText={`${(
            (analytics?.revenue?.last_7_days_trend_percentage || 0) * 100
          ).toFixed(2)}% in last 7 days`}
          hasIncreased={analytics?.revenue?.last_7_days_trend === 'up'}
        />
        {/* Hide this button for now */}
        {/* <RegistrationIndicatorCard
          iconName='download'
          title='Report'
          renderContent={() => {
            return (
              <div
                css={css`
                  margin-top: 8px;
                `}
              >
                <a
                  css={css`
                    font-weight: 700;
                    font-size: 16px;
                    letter-spacing: 0.05em;
                    margin-top: 8px;
                    text-decoration: underline;
                  `}
                  ref={downloadRef}
                  onClick={onView}
                >
                  Download CSV
                </a>
              </div>
            )
          }}
        /> */}
      </div>
    </Flex>
  )
}

export default observer(Registration)
